/**
 * segLean (INV-HLP-10 / INV-ENS-02, ENGINE-SPEC §7.5): the seed's per-paragraph machine-lean
 * estimate in [−1, 1], ported without changing its arithmetic. Equality with the seed oracle's
 * `segLean` on paragraphs of the sample and on fuzz strings is test T-ENS-10.
 *
 * Differences from the seed, all invisible on text that does not use the JS `Object` prototype
 * words as vocabulary:
 * - TELLS is a `Map`, not a plain object (the seed's `TELLS['constructor']` hits an inherited
 *   function and poisons the sum with NaN; candidate change C-LEX-PROTO, as in the compiled LX-01).
 * - Speed only: a regex is run only when its required literals occur in the paragraph. The
 *   contrast regexes use the registered prefilters (T-LEX-05 proves them); the phrase regexes
 *   are admitted by the Aho–Corasick matcher (the compiled LX-02 matcher, which reproduces the
 *   regex hit set) and the exact regex then supplies the count, so a hit is never approximated.
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { foldAsciiString } from '../lexicons/chars.ts';
import { compileEnglishLexicons } from '../lexicons/compile.ts';
import type { PhraseMatcher } from '../lexicons/phrase-matcher.ts';
import { clamp } from '../util/stats.ts';

interface Compiled {
	tells: Map<string, number>;
	phraseRes: RegExp[];
	phraseMatcher: PhraseMatcher;
	phraseCount: number;
	contrast: RegExp[];
	/** Per contrast regex: indices into `lits` of its required literals (empty → always run). */
	contrastLits: number[][];
	lits: Array<{ text: string; fold: boolean }>;
	word: RegExp;
	dash: RegExp;
	absolute: RegExp;
	modalPassive: RegExp;
	questionAnswer: RegExp;
	fillers: RegExp;
	repeatedPunct: RegExp;
	initialConj: RegExp;
	contraction: RegExp;
}

let compiled: Compiled | undefined;
const seen = new Uint8Array(256);
const presentBuf = new Uint8Array(256);

function build(): Compiled {
	const lex = loadEnglishLexicons();
	const c = compileEnglishLexicons(lex);
	const tells = new Map<string, number>();
	for (const [w, x] of lex.tells.entries) tells.set(w, x);
	const litIndex = new Map<string, number>();
	const lits: Array<{ text: string; fold: boolean }> = [];
	const pf = lex.prefilters.lists['contrast-res'] ?? [];
	const contrastLits = c.contrast.regexes.map((re, i) => {
		const fold = re.flags.includes('i');
		return (pf[i] ?? []).map((l) => {
			const text = fold ? foldAsciiString(l) : l;
			const key = `${fold ? 'f' : 'x'}:${text}`;
			let idx = litIndex.get(key);
			if (idx === undefined) {
				idx = lits.length;
				lits.push({ text, fold });
				litIndex.set(key, idx);
			}
			return idx;
		});
	});
	const g = c.segLean;
	return {
		tells,
		phraseRes: lex.phraseRes.entries.map((e) => new RegExp(e.source, e.flags)),
		phraseMatcher: c.phrases,
		phraseCount: c.phraseCount,
		contrast: [...c.contrast.regexes],
		contrastLits,
		lits,
		word: g.get('wordToken'),
		dash: g.get('dashClass'),
		absolute: g.get('absoluteAdverbs'),
		modalPassive: g.get('modalPassiveShort'),
		questionAnswer: g.get('questionShortAnswer'),
		fillers: g.get('fillers'),
		repeatedPunct: g.get('repeatedPunctOrEllipsis'),
		initialConj: g.get('initialConjunction'),
		contraction: g.get('contractionShort'),
	};
}

const count = (re: RegExp, s: string): number => (s.match(re) || []).length;

/** Per-paragraph machine-lean estimate in [−1, 1], or null when the paragraph has fewer than 12 words. */
export function segLean(pText: string): number | null {
	if (!compiled) compiled = build();
	const C = compiled;
	const lower = pText.toLowerCase();
	const w = lower.match(C.word) || [];
	if (w.length < 12) return null;
	let mach = 0;
	let hum = 0;
	for (const x of w) {
		const t = C.tells.get(x);
		if (t !== undefined) mach += t;
	}
	const folded = foldAsciiString(pText);

	// phrases: the AC matcher names the phrases present; the exact regex counts them
	C.phraseMatcher.matchAll(pText, 0, (_s, _e, idx) => {
		if (idx >= C.phraseCount) return;
		// counted below, once per phrase
		seen[idx] = 1;
	});
	for (let i = 0; i < C.phraseCount; i++) {
		if (!seen[i]) continue;
		seen[i] = 0;
		const m = pText.match(C.phraseRes[i] as RegExp);
		if (m) mach += 2.5 * m.length;
	}

	// contrast list: required literals checked once each
	const present = presentBuf;
	present.fill(0);
	for (let i = 0; i < C.contrast.length; i++) {
		const need = C.contrastLits[i] as number[];
		let ok = need.length === 0;
		for (let q = 0; q < need.length && !ok; q++) {
			const li = need[q] as number;
			let st = present[li] as number;
			if (st === 0) {
				const l = C.lits[li] as { text: string; fold: boolean };
				st = (l.fold ? folded : pText).includes(l.text) ? 2 : 1;
				present[li] = st;
			}
			if (st === 2) ok = true;
		}
		if (!ok) continue;
		const m = pText.match(C.contrast[i] as RegExp);
		if (m) mach += 2.5 * m.length;
	}

	if (pText.includes('—') || pText.includes('–')) mach += 2 * count(C.dash, pText);
	mach += 0.9 * count(C.absolute, pText);
	mach += 1.4 * count(C.modalPassive, pText);
	const hasQ = pText.includes('?');
	if (hasQ) hum += 1.5 * count(C.questionAnswer, pText);
	hum += count(C.fillers, pText);
	if (hasQ || pText.includes('!') || pText.includes('...') || pText.includes('…'))
		hum += 1.2 * count(C.repeatedPunct, pText);
	hum += C.initialConj.test(pText) ? 0.8 : 0;
	if (pText.includes("'") || pText.includes('’')) hum += count(C.contraction, pText) > 2 ? 0.6 : 0;
	const net = (mach - hum) / Math.max(4, w.length / 22);
	return clamp(Math.tanh(net * 0.8), -1, 1);
}
